import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

const key = (ctx, pid) => `dd_camfb_${ctx}_${pid}`;

// Lightweight yes/no value-prompt shown once per property (dismissal persisted by
// property_id). Optimistic + non-blocking: hides immediately, submits in background.
export function CameraFeedback({ propertyId, context }) {
  const [gone, setGone] = useState(() => {
    try { return !!localStorage.getItem(key(context, propertyId)); } catch { return false; }
  });
  const [comment, setComment] = useState("");

  if (gone || !propertyId) return null;

  const persist = () => { try { localStorage.setItem(key(context, propertyId), "1"); } catch { /* ignore */ } };

  const submit = (answer) => {
    persist();
    setGone(true);
    toast.success("Thanks — this helps other renters.");
    api.post("/feedback", {
      property_id: propertyId,
      answer,
      comment: comment.trim().slice(0, 280) || null,
      context,
    }).catch((e) => console.error("feedback submit failed", e));
  };

  const skip = () => { persist(); setGone(true); };

  return (
    <div data-testid={`camera-feedback-${context}`} className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-navy">Would you have documented this thoroughly with just your camera?</p>
        <button data-testid="camera-feedback-close" onClick={skip} aria-label="Skip" className="text-[#94A3B8] hover:text-[#475569] shrink-0">
          <X size={16} />
        </button>
      </div>
      <Input
        data-testid="camera-feedback-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={280}
        placeholder="Add a comment (optional)"
        className="mt-3 rounded-xl text-sm"
      />
      <div className="mt-3 flex items-center gap-2">
        <Button data-testid="camera-feedback-yes" onClick={() => submit("yes")} variant="outline" className="rounded-xl border-[#E2E8F0]">Yes</Button>
        <Button data-testid="camera-feedback-no" onClick={() => submit("no")} variant="outline" className="rounded-xl border-[#E2E8F0]">No</Button>
        <button data-testid="camera-feedback-skip" onClick={skip} className="ml-auto text-sm text-[#94A3B8] hover:text-[#475569]">Skip</button>
      </div>
    </div>
  );
}
